import createClient from "openapi-fetch";
import type { paths } from "./generated/rails-api";
import { env, isFrontend, isBackend } from "./env";

let jwt: typeof import('jsonwebtoken') | null = null;
let crypto: typeof import('crypto') | null = null;
let backendModulesPromise: Promise<void> | null = null;

async function loadBackendModules() {
  if (!isBackend()) return;

  if (!backendModulesPromise) {
    backendModulesPromise = (async () => {
      const jwtModule = await import('jsonwebtoken');
      jwt = jwtModule.default || jwtModule;
      crypto = await import('crypto');
    })();
  }

  return backendModulesPromise;
}

/** Default total request timeout (10 seconds) */
const DEFAULT_TIMEOUT_MS = 10_000;

/** Default connect timeout — abort if no response headers within this window (5 seconds) */
const DEFAULT_CONNECT_TIMEOUT_MS = 5_000;

export interface RailsApiClientOptions {
  jwt?: string;
  baseUrl?: string;
  /**
   * When true, uses internal service-to-service auth (X-Signature + X-Timestamp)
   * instead of user JWT authentication. Use for endpoints that have
   * `skip_before_action :require_api_authentication` and use `verify_internal_api_request`.
   */
  internalServiceCall?: boolean;
  /**
   * Total request timeout in milliseconds. Defaults to 10 seconds.
   * Set to 0 to disable timeout.
   */
  timeoutMs?: number;
  /**
   * Connect timeout in milliseconds — aborts if no response headers
   * received within this window. For inter-service calls, this should
   * be shorter than timeoutMs to fail fast on unreachable services.
   * Defaults to 5 seconds. Set to 0 to disable.
   */
  connectTimeoutMs?: number;
}

function generateSignature(timestamp: number): string {
  if (!crypto) {
    throw new Error('crypto module not loaded - call loadBackendModules first');
  }
  const secret = env.JWT_SECRET || 'test-secret-key';
  return crypto.createHmac('sha256', secret).update(timestamp.toString()).digest('hex');
}

const testHeaders = (baseHeaders: Record<string, string>) => {
  if (!jwt) {
    throw new Error('jsonwebtoken module not loaded - call loadBackendModules first');
  }
  const jwtSecret = env.JWT_SECRET || 'test-secret-key';
  const testTimestamp = Date.now();
  
  const proof = jwt.sign(
      { timestamp: testTimestamp, mode: 'test' },
      jwtSecret,
      { expiresIn: '1m', algorithm: 'HS256' }
  );
  return {
    ...baseHeaders,
    "X-Test-Mode": "true",
    "X-Test-Proof": proof,
  }
}

const sharedHeaders = (): Record<string, string> => {
  return {
    "Accept": "application/json",
  };
}

const addBackendHeaders = (headers: Record<string, string>, jwtToken?: string) => {
  if (!isFrontend()) {
    const timestamp = Math.floor(Date.now() / 1000);
    // Only add Authorization header if we have a JWT (user-authenticated requests)
    if (jwtToken) {
      headers["Authorization"] = `Bearer ${jwtToken}`;
    }
    headers["X-Signature"] = generateSignature(timestamp);
    headers["X-Timestamp"] = timestamp.toString();

    if (env.NODE_ENV === "test") {
      const testHeadersResult = testHeaders(headers);
      return testHeadersResult;
    }
  }
  return headers;
}

const headers = (jwtToken?: string, internalServiceCall?: boolean) => {
  let headers: Record<string, string> = sharedHeaders();

  if (isBackend()) {
    if (internalServiceCall) {
      // Internal service calls: signature only, no JWT
      headers = addBackendHeaders(headers);
    } else if (jwtToken) {
      // User-authenticated calls: JWT + signature
      headers = addBackendHeaders(headers, jwtToken);
    }
  }
  return headers;
}

/**
 * Build the full set of Rails-API auth headers for a given options bag.
 * Exported so subclasses of RailsAPIBase that need to make raw fetch calls
 * (e.g. MCPAPIService's dynamic dispatch) can reuse the exact same auth
 * machinery the typed openapi-fetch client uses.
 *
 * On backend, this includes X-Signature/X-Timestamp HMAC and (in test env)
 * X-Test-Mode/X-Test-Proof headers. The backend module loading (jsonwebtoken,
 * crypto) is awaited internally so callers don't need to know about it.
 */
export async function buildAuthHeaders(
  options: { jwt?: string; internalServiceCall?: boolean } = {},
): Promise<Record<string, string>> {
  if (isBackend()) await loadBackendModules();
  return headers(options.jwt, options.internalServiceCall);
}

/**
 * Creates a fetch wrapper with dual timeout support.
 *
 * - connectTimeoutMs: abort if no response headers received (fast-fail on unreachable services)
 * - timeoutMs: abort if entire request exceeds this duration
 *
 * Exported for subclasses of RailsAPIBase that bypass openapi-fetch but
 * still want the connect-vs-total timeout discipline (e.g. MCPAPIService).
 */
export function createFetchWithTimeout(timeoutMs: number, connectTimeoutMs: number): typeof fetch {
  if (timeoutMs <= 0 && connectTimeoutMs <= 0) {
    return fetch;
  }

  return async (input, init) => {
    const controller = new AbortController();
    let timedOutBy: "connect" | "total" | null = null;

    // Connect timeout — fires if we don't get response headers quickly
    const connectTimer = connectTimeoutMs > 0
      ? setTimeout(() => { timedOutBy = "connect"; controller.abort(); }, connectTimeoutMs)
      : null;

    // Total timeout — fires if entire request takes too long
    const totalTimer = timeoutMs > 0
      ? setTimeout(() => { timedOutBy = timedOutBy ?? "total"; controller.abort(); }, timeoutMs)
      : null;

    try {
      const response = await fetch(input, {
        ...init,
        signal: controller.signal,
      });
      // Got response headers — clear connect timeout, keep total timeout for body
      if (connectTimer) clearTimeout(connectTimer);
      return response;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        if (timedOutBy === "connect") {
          throw new Error(`Connect timed out after ${connectTimeoutMs}ms`);
        }
        throw new Error(`Request timed out after ${timeoutMs}ms`);
      }
      throw error;
    } finally {
      if (connectTimer) clearTimeout(connectTimer);
      if (totalTimer) clearTimeout(totalTimer);
    }
  };
}

/**
 * Creates a typed client for the Rails API
 * @param options - Configuration options for the client
 * @returns A typed openapi-fetch client
 */
export async function createRailsApiClient(options: RailsApiClientOptions = {}) {
  // Disable timeout in tests to avoid HTTP interceptor compatibility issues
  const isTest = env.NODE_ENV === 'test';
  const defaultTimeout = isTest ? 0 : DEFAULT_TIMEOUT_MS;
  const defaultConnectTimeout = isTest ? 0 : DEFAULT_CONNECT_TIMEOUT_MS;

  const {
    jwt: jwtToken,
    baseUrl = env.RAILS_API_URL || env.VITE_RAILS_API_URL || (isFrontend() ? (globalThis as any).location?.origin ?? "http://localhost:3000" : "http://localhost:3000"),
    internalServiceCall = false,
    timeoutMs = defaultTimeout,
    connectTimeoutMs = defaultConnectTimeout,
  } = options;

  if (isBackend()) {
    await loadBackendModules();
  }

  const client = createClient<paths>({
    baseUrl,
    headers: headers(jwtToken, internalServiceCall),
    // Include credentials (cookies) for frontend requests to enable session auth
    ...(isFrontend() && { credentials: 'include' }),
    // Add timeout support via custom fetch (connect + total)
    fetch: createFetchWithTimeout(timeoutMs, connectTimeoutMs),
  });

  return client;
}

/**
 * Helper type to extract response types from the API
 */
export type ApiResponse<T extends keyof paths, M extends keyof paths[T]> =
  paths[T][M] extends { responses: infer R }
    ? R extends { 201: { content: { "application/json": infer C } } }
      ? C
      : R extends { 200: { content: { "application/json": infer C } } }
      ? C
      : never
    : never;

/**
 * Helper type to extract request body types from the API
 */
export type ApiRequestBody<T extends keyof paths, M extends keyof paths[T]> =
  paths[T][M] extends { requestBody: { content: { "application/json": infer B } } }
    ? B
    : never;
